import moment from 'moment-timezone';
import {EMPTY, forkJoin, of, Subject, Subscription} from 'rxjs';
import {expand, finalize, map, reduce, switchMap, takeUntil, tap} from 'rxjs/operators';
import {AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Injector, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, ViewChild} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ngfSelect} from 'angular-file';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {NgbModal, NgbModalOptions} from '@ng-bootstrap/ng-bootstrap';
import {ToasterService} from '@app/shared/components/toaster/toaster.service';
import {HtmlUtils} from '@app/shared/util/general/html-utils';
import {CollectionUtils} from '@app/shared/util/collections/collection-utils';
import {Toast, ToastType} from '@app/shared/components/toaster/Toast';
import {EmailTicketModalComponent} from '@app/layout/sites/tickets/email-ticket-modal/email-ticket-modal.component';
import {AlertTicket, EmailTrackingCampaignName, EscalatedTicket, Ticket, TicketAPIResultFull, TicketAttachment, TicketChangeHistory, TicketReferenceData, TicketsService, TicketStatusId, TicketType} from '@app/shared/services';
import {UserProfileService} from '@app/shared/services/api/user-profile.service';
import {UpsertTicketCommentModalComponent} from '../upsert-ticket-comment-modal/upsert-ticket-comment-modal.component';
import {UpsertTicketCommentComponent, UpsertTicketCommentProgress, UpsertTicketCommentResult, UpsertTicketCommentState} from '../upsert-ticket-comment/upsert-ticket-comment.component';
import {TicketTrackingEvent, TicketTrackingService} from '@app/layout/sites/tickets/ticket-tracking.service';
import {EmailType} from '@app/layout/sites/tickets/email-ticket/email-ticket.component';
import {EscalatedTicketUpsertModalComponent} from '@app/layout/sites/tickets/escalated-ticket-upsert-modal/escalated-ticket-upsert-modal.component';
import {User} from '@app/layout/admin/services/api/user-interfaces';
import {AddCommentConfig, ESCALATED_TICKETS_EXECUTABLE_ACTIONS_BY_STATUS_ID, EscalatedTicketExecutableAction, TicketComponentPart, TicketComponentWithAttachmentPreview, TicketOperation, TicketOperationResult} from '@app/layout/sites/tickets/tickets.interface';
import {SiteInfo} from '@shared/services/api/access-sites.interface';
import {LinkifyService} from '@shared/services/ui/linkify/linkify.service';
import {AppConstants} from '@app/shared/util/app-constants';
import {ResizeEvent} from 'angular-resizable-element';
import {BrowserUtils} from '@shared/util/general/browser-utils';
import {FormControl, UntypedFormControl} from '@angular/forms';
import {LinkActionToAlertsCallbackFns, TicketUtils} from '@app/layout/sites/tickets/ticket-utils';
import {TicketSelectComponent} from '@app/layout/sites/tickets/ticket-select/ticket-select.component';
import {AlertTicketDetailsModal, AlertTicketDetailsModalComponent} from '@app/layout/sites/tickets/alert-ticket-details-modal/alert-ticket-details-modal.component';
import {TicketsConstants} from '@shared/services/api/tickets/tickets-constants';
import {Task} from "@shared/services/api/task.interface";
import {TicketDataCaches} from "@shared/data-stores/ticket-data-caches";
import {Equipment, MetadataType, MetadataTypeId} from "@app/graphql-typings/graphql";
import {CanDeactivateComponent} from '@app/shared/api/candeactivate/can-deactivate';
import {DeactivateModalOptions} from '@app/shared/util/general/deactivate-utils';
import {AttachmentDeleteAccess, AttachmentPreviewUserAction, AttachmentsComponent} from '@app/shared/components/attachments/attachments/attachments.component';
import {AttachmentServiceToken} from '@app/shared/components/attachments/attachment.service';
import {AssociateAttachmentRequestBody, TicketAttachmentService} from './ticket-attachment.service';
import {UploadStateComponent} from '@app/shared/components/attachments/upload-state/upload-state.component';
import {TicketActionStateManager} from '../ticket-action-state-manager';
import {DurationInputModalComponent} from "@shared/components/duration-input-modal/duration-input-modal.component";
import {FeatureService} from "@shared/services/feature/feature-service";


@Component({
  selector: 'app-escalated-ticket-body',
  templateUrl: './escalated-ticket-body.component.html',
  styleUrls: ['./escalated-ticket-body.component.scss'],
  providers: [{provide: AttachmentServiceToken, useClass: TicketAttachmentService}],
  standalone: false
})
export class EscalatedTicketBodyComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy, TicketComponentPart, CanDeactivateComponent, TicketComponentWithAttachmentPreview {

  @Input() ticket: EscalatedTicket;
  @Input() site: SiteInfo;
  @Input() addCommentConfig: AddCommentConfig;
  @Input() showAttachmentId: string;
  @Input() hideInboxControl = false;
  @Input() showViewLinkedTicket = true;
  @Input() readOnly = false;

  @Output() ticketOperation: EventEmitter<TicketOperationResult<EscalatedTicket>> = new EventEmitter();
  @Output() attachmentPreviewUserAction = new EventEmitter<AttachmentPreviewUserAction>();

  @ViewChild('commentUploadButtonSelectDirective', { read: ngfSelect, static: false }) commentUploadButtonSelectDirective: ngfSelect;
  @ViewChild('commentComponent', { static: false }) commentComponent: UpsertTicketCommentComponent;
  @ViewChild('activityContainer', { static: false }) activityContainer: ElementRef<HTMLElement>;
  @ViewChild('linkedAlertsSelect', { static: true }) linkedAlertSelect: TicketSelectComponent;
  @ViewChild(AttachmentsComponent, { static: true }) attachmentsComponent: AttachmentsComponent<TicketAttachment, string, AssociateAttachmentRequestBody>;
  @ViewChild(UploadStateComponent) uploadStateComponent: UploadStateComponent<TicketAttachment, string, AssociateAttachmentRequestBody>;

  readonly AttachmentDeleteAccess = AttachmentDeleteAccess;
  readonly DEFAULT_LOGO_CONTAINER_WIDTH = AppConstants.DEFAULT_LOGO_CONTAINER_WIDTH;
  readonly DEFAULT_ACTIVITY_CONTAINER_HEIGHT = 200;
  readonly MENTION_CHARACTER = TicketUtils.MENTION_CHARACTER;

  private readonly commentDeactivateOption: DeactivateModalOptions = {
    title: {messageKey: 'Discard your comment?'},
    body: {messageKey: 'Do you want to discard your comment?'},
    yesText: {messageKey: 'Keep Writing'},
    noText: {messageKey: 'Discard'},
    deactivateOnCancel: true,
    afterConfirmFn: () => this.commentComponent?.focus()
  };
  private readonly uploadingDeactivateOption: DeactivateModalOptions = {
    title: {messageKey: 'Cancel uploading?'},
    body: {messageKey: 'Do you want to cancel file uploads?'},
    yesText: {messageKey: 'Keep uploading'},
    noText: {messageKey: 'Cancel uploading'},
    deactivateOnCancel: true,
  };
  private readonly COPY_TO_CLIPBOARD_TOAST: Toast = {
    message: {messageKey: 'Copied to clipboard'},
    type: ToastType.Success,
    timeout: 2000,
    toastId: this.toasterService.generateToasterId()
  };
  private readonly TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss';
  private readonly NEW_LINES_REG_EXP = /([^\r\n]+)/g;

  linkedAlertsControl: UntypedFormControl;
  TicketType = TicketType;
  ExecutableAction = EscalatedTicketExecutableAction;
  TicketStatusId = TicketStatusId;
  createdAt: string;
  resolvedAt: string;
  updatedAt: string;
  updating = false;
  collapsedFields: { collapsedDescription: string, collapsedSolution: string } = {
    collapsedDescription: null,
    collapsedSolution: null
  };
  manuallySetHeight = false;
  activityContainerHeight = this.DEFAULT_ACTIVITY_CONTAINER_HEIGHT;
  // AP-2944 - When displaying the ticket fields collapsed by default users are having trouble discovering that fields can be expanded. See
  // how things go when the ticket fields are expanded by default
  fieldsCollapsed = false;
  commentsCollapsed = true;
  loadingActivity = false;
  loadingChangeHistory = false;
  showChangeHistory = false;
  doesActivityScrollbarExist: boolean = null;
  commentBoxFocused = false;
  // Used to save any comment text that existed when comments are collapsed, so that the text can be restored if comments
  // are expanded
  savedCommentText: string;
  isMobile: boolean;
  mentionLastFocused = false;
  assigneesById: Map<string, User>;
  assigneesLoading = false;
  attachmentDeleteAccess: AttachmentDeleteAccess;
  attachmentAddAllowed: boolean;

  private readonly loadActivityEvent = new Subject<boolean>();
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly logger: AceLogger;

  // As comments could be created and/or deleted and change history could be created on the server side since the ticket
  // (with its comments and change history) has been fetched, which could result in fetching some of the same comments
  // and/or change history again, need to:
  // 1. Keep track of the comment and change history record ids to ensure the same record is not shown twice
  // 2. Keep an explicit track of the offset used to load the next set, to ensure that even if the total number of fetched
  // is less than the total count on the ticket, we know when to stop fetching
  private commentIds: Set<string>;
  private commentOffset: number;
  private changeHistoryIds: Set<string>;
  private changeHistoryOffset: number;

  private isActivityScrollbarAtBottom: boolean = null;
  private activityScrollDelayHandle: any;
  private calcScrollBarDelayHandle: any;
  private commentBoxFocusedDelayHandle: any;
  private ticketsById: Map<string, Ticket> = new Map();
  private modal: any;
  private ticketModalInjector: Injector;
  private metadataTypeSubscription: Subscription;

  equipmentLoading = false;

  equipmentById: Map<number, Equipment>;
  metadataTypesById: Map<MetadataTypeId, MetadataType>;
  tasksById: Map<number, Task>;
  tasksLoading = false;

  assigneesByEntityName: Map<string, User[]>;
  assigneesByEmail: Map<string, User>;
  mobileMentionControl: FormControl<User>;
  exportTicketLink: string;
  hasCommentAttachments = false;
  commentAttachmentUploading = false;
  attachmentUploading = false;
  showTimeTaken = false;

  constructor(private cdRef: ChangeDetectorRef,
              private modalService: NgbModal,
              private toasterService: ToasterService,
              private userProfileService: UserProfileService,
              private ticketsService: TicketsService,
              private ticketTrackingService: TicketTrackingService,
              private linkifyService: LinkifyService,
              private ticketDataCaches: TicketDataCaches,
              private featureService: FeatureService,
              private injector: Injector,
              @Optional() private actionStateManager: TicketActionStateManager<EscalatedTicketExecutableAction>,
              logService: AceLoggerService) {
    this.logger = logService.create(EscalatedTicketBodyComponent.name);
    this.actionStateManager ??= new TicketActionStateManager(ESCALATED_TICKETS_EXECUTABLE_ACTIONS_BY_STATUS_ID);
    const user = userProfileService.getLoggedInUser();
    this.attachmentAddAllowed = !!user;
    this.attachmentDeleteAccess = userProfileService.isPlatformAdminOrCustomerAdmin(user)
      ? AttachmentDeleteAccess.ALLOWED
      : AttachmentDeleteAccess.ALLOWED_OWN;
  }

  ngOnInit() {
    this.isMobile = BrowserUtils.isMobile();
    this.savedCommentText = this.addCommentConfig?.commentText;
    const activityContainerHeight = this.userProfileService.getLoggedInUser()?.nonPersistedPreferences?.tickets?.view?.activityContainerHeight;
    if (activityContainerHeight) {
      this.activityContainerHeight = activityContainerHeight;
      this.manuallySetHeight = true;
    }

    this.loadActivityEvent.pipe(
        takeUntil(this.ngUnsubscribe),
        switchMap(showLoadChangeHistory => {
          this.loadingActivity = true;
          if (showLoadChangeHistory) {
            this.loadingChangeHistory = true;
          }
          return forkJoin([
            this.haveMoreCommentsToLoad() ?
                this.ticketsService.getCommentsForEscalatedTicket(this.ticket.ticket_id, this.commentOffset, TicketsConstants.COMMENT_FETCH_SIZE)
                    .pipe(map(({ count: count, comments: comments }) => ({ count: count, comments: comments, commentsFetched: true })))
                : of({ count: null, comments: null, commentsFetched: false }),
            this.showChangeHistory && this.haveMoreChangeHistoryToLoad() ?
                this.ticketsService.getChangeHistoryForEscalatedTicket(this.ticket.ticket_id, this.changeHistoryOffset, TicketsConstants.CHANGE_HISTORY_FETCH_SIZE)
                    .pipe(map(({ count: historyCount, change_history: changeHistory }) => ({ count: historyCount, change_history: changeHistory, changeHistoryFetched: true })))
                : of({ count: null, change_history: null, changeHistoryFetched: false }),
            of(showLoadChangeHistory)
          ]);
        })
    ).subscribe(([{ count: count, comments: comments, commentsFetched }, { count: historyCount, change_history: changeHistory, changeHistoryFetched }, showLoadChangeHistory]) => {
      if (!this.ticket.comments) {
        this.ticket.comments = [];
      }
      const numberOfComments = this.ticket.comments.length;

      if (commentsFetched) {
        // If the comments were fetched, no matter how many were fetched, increment commentOffset to ensure we never fetch
        // the same range again, to ensure we do not go into an infinite loop
        this.commentOffset += TicketsConstants.COMMENT_FETCH_SIZE;
        if (comments) {
          this.ticket.comment_count = count;
          comments.forEach(comment => {
            if (!this.commentIds.has(comment.comment_id)) {
              this.commentIds.add(comment.comment_id);
              this.ticket.comments.push(comment);
            }
          });
        }
      }
      this.loadingActivity = false;

      if (!this.ticket.change_history) {
        this.ticket.change_history = [];
      }

      let fetchedViewableChangeHistory = false;
      if (changeHistoryFetched) {
        // If change history was fetched, no matter how many records were fetched, increment changeHistoryOffset to ensure
        // we never fetch the same range again, to ensure we do not go into an infinite loop
        this.changeHistoryOffset += TicketsConstants.CHANGE_HISTORY_FETCH_SIZE;
        if (changeHistory) {
          this.ticket.change_history_count = historyCount;
          changeHistory.forEach((history: TicketChangeHistory) => {
            if (!this.changeHistoryIds.has(history.ticket_change_history_id)) {
              this.changeHistoryIds.add(history.ticket_change_history_id);
              this.ticket.change_history.push(history);
              fetchedViewableChangeHistory = fetchedViewableChangeHistory || (this.ticketsService.transformToViewableChangeHistoryRecord(TicketType.ESCALATED, history) != null);
            }
          });
        }
      }
      this.loadingChangeHistory = false;

      if ((!commentsFetched || this.ticket.comments.length !== numberOfComments) &&
          (!changeHistoryFetched || fetchedViewableChangeHistory)) {
        if (commentsFetched) {
          this.ticket.comments = [...this.ticket.comments];
        }
        if (changeHistoryFetched) {
          this.ticket.change_history = [...this.ticket.change_history];
        }
        // Ensure the new comments and the new change history records are passed back to the main page so that they
        // do not have to be fetched again, if the ticket modal is closed and reopened
        this.onTicketChange();
        // Recalculate scrollbar state as new comments and/or new change history records have been loaded
        this.resetActivityScrollBarStateCalculation();
      } else {
        // If all the activity we have retrieved are the ones we already have (due to many new comments being created on the server
        // since the ticket has been fetched), then fetch more activity (if there is to fetch), as otherwise infinite
        // scroll will think that everything has been fetched and will stop generating a scroll event
        this.loadActivityIfNecessary(showLoadChangeHistory);
      }
    });
    /** keeps mention for mobile empty */
    this.mobileMentionControl = new FormControl<User>(null);
    this.mobileMentionControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(assignee => {
      this.addMention(assignee);
      this.mobileMentionControl.reset(null, {emitEvent: false});
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes.ticket) {
        this.showTimeTaken = this.featureService.timeTakenEnabled(this.site);
        this.exportTicketLink = TicketUtils.makeExportTicketLink(this.ticket.site_id, this.ticket.ticket_id);
        this.commentIds = new Set<string>();
        this.changeHistoryIds = new Set<string>();
        this.commentOffset = 0;
        this.changeHistoryOffset = 0;
        this.linkedAlertsControl = new UntypedFormControl();
        if (this.ticket) {
          this.refreshEquipment();
          this.refreshMetadataTypes();
          this.refreshAssignees();
          this.refreshTicketCalculatedFields();

          TicketUtils.registerTicketInMap(this.ticket, this.ticketsById);

          this.collapsedFields = {
            collapsedDescription: this.collapseFieldValue(this.ticket.description),
            collapsedSolution: this.collapseFieldValue(this.ticket.solution),
          };

          if (this.ticket.comments) {
            this.ticket.comments.forEach(comment => this.commentIds.add(comment.comment_id));
            this.commentOffset = this.ticket.comments.length;
          }

          if (this.ticket.change_history) {
            this.ticket.change_history.forEach(ch => this.changeHistoryIds.add(ch.ticket_change_history_id));
            this.changeHistoryOffset = this.ticket.change_history.length;
          }
          if (this.ticket.comment_count == null) {
            this.logger.error('Ticket property comment_count, is not initialised', this.ticket);
          } else if (this.ticket.comment_count === 0) {
            this.setShowChangeHistory(true);
          }
        } else {
          this.collapsedFields = { collapsedDescription: null, collapsedSolution: null };
        }
      }
    }
  }

  ngAfterViewChecked(): void {
    if (this.doesActivityScrollbarExist == null || this.isActivityScrollbarAtBottom == null) {
      if (this.calcScrollBarDelayHandle != null) {
        clearTimeout(this.calcScrollBarDelayHandle);
      }
      const oldValue = !!this.doesActivityScrollbarExist;
      // TODO Use ResizeObserver.
      // Sometimes the browser needs some time to resize the elements and therefore we need to delay
      // doing the calculation. Need to convert to using ResizeObserver to avoid this delay mess.
      this.calcScrollBarDelayHandle = setTimeout(() => {
        this.calculateCanScrollActivity();
        // If no scrollbar is shown see if there is more activity and retrieve it, to make the scrollbar appear so that
        // infinite scroll can be triggered when the user scrolls. That is, activity will be retrieved until all
        // activity is retrieved, or there is enough activity to create a scrollbar which will trigger infinite scroll
        // events when the user scrolls to the bottom, to retrieve further activity
        if (!this.doesActivityScrollbarExist) {
          this.loadActivityIfNecessary(false);
        }
        if (!!this.doesActivityScrollbarExist !== oldValue) {
          // We need for the activity container (div) to render before we can calculate whether the container needs a scrollbar.
          // However, if the value of doesActivityScrollbarExist changes the ExpressionChangedAfterItHasBeenCheckedError is thrown. So need to
          // run change detection explicitly.
          this.cdRef.detectChanges();
        }
        this.calcScrollBarDelayHandle = null;
      }, 200);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.metadataTypeSubscription?.unsubscribe();
  }

  @HostListener('click', ['$event'])
  filterAndOpenAttachmentLinks(event: MouseEvent): boolean {
    if (this.isDetectedLink(event)) {
      const linkUrl = new URL((<HTMLAnchorElement>event.target).href);
      if (this.linkifyService.isPlatformURL(linkUrl)) {
        const urlSegments = HtmlUtils.getPathSegments(linkUrl);
        const attachmentInfo = TicketUtils.getAttachmentUrlData(urlSegments);
        if (attachmentInfo && attachmentInfo.ticket_id === this.ticket.ticket_id) {
          this.attachmentsComponent.openAttachmentById(attachmentInfo.attachment_id);
          return false;
        }
      }
    }
    return true;
  }

  private formatTimestampInSiteTimezone(ts: moment.Moment): string | null {
    return ts
      ? (this.site?.timezone
        ? moment.utc(ts).tz(this.site.timezone)
        : ts).format(this.TIMESTAMP_FORMAT)
      : null;
  }

  private refreshTicketCreatedAt() {
    this.createdAt = this.formatTimestampInSiteTimezone(this.ticket.created_at);
  }

  private refreshTicketResolvedAt() {
    this.resolvedAt = this.formatTimestampInSiteTimezone(this.ticket.resolved_at);
  }

  private refreshTicketUpdatedAt() {
    this.updatedAt = this.formatTimestampInSiteTimezone(this.ticket.updated_at);
  }

  private refreshLinkedAlerts() {
    const linkedAlerts = <AlertTicket[]>TicketUtils.getActionLinkedAlerts(this.ticket);
    this.reloadTasks(linkedAlerts?.map(alert => alert.rule_id));
    this.linkedAlertsControl.setValue(linkedAlerts, { emitEvent: false });
  }

  private refreshMetadataTypes() {
    this.metadataTypeSubscription?.unsubscribe();
    this.metadataTypeSubscription = this.ticketDataCaches.getMetadataTypesByIdBySiteId(this.ticket.site_id)
      .subscribe(metadataTypesById => this.metadataTypesById = metadataTypesById);
  }

  private refreshEquipment() {
    this.equipmentLoading = true;
    this.ticketDataCaches.getEquipmentBySiteId(this.ticket.site_id)
      .pipe(finalize(() => this.equipmentLoading = false))
      .subscribe(equipmentById => this.equipmentById = equipmentById);
  }

  private refreshTicketCalculatedFields() {
    this.refreshTicketCreatedAt();
    this.refreshTicketResolvedAt();
    this.refreshTicketUpdatedAt();
    this.refreshLinkedAlerts();
  }

  private refreshAssignees() {
    this.assigneesLoading = true;
    this.ticketDataCaches.getAssigneesBySiteId(this.ticket.site_id)
      .pipe(finalize(() => this.assigneesLoading = false))
      .subscribe(assigneesById => {
        const assigneesByEntityName = new Map<string, User[]>();
        const assigneesByEmail = new Map<string, User>();
        this.assigneesById = assigneesById;
        this.assigneesById.forEach(assignee => {
          const entityName = assignee.entity.name;
          assigneesByEntityName.set(entityName, [...assigneesByEntityName.get(entityName) || [], assignee]);
          assigneesByEmail.set(assignee.email, assignee);
        });
        this.assigneesByEntityName = assigneesByEntityName;
        this.assigneesByEmail = assigneesByEmail;
      });
  }

  private saveTypedComment() {
    this.savedCommentText = this.commentComponent?.getText() || null;
  }

  get ticketReferenceData(): TicketReferenceData {
    return {
      assigneesById: this.assigneesById,
    };
  }

  email() {
    this.ticketTrackingService.trackTicket(TicketTrackingEvent.FORWARD, this.ticket);
    const modalRef = this.modalService.open(EmailTicketModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: true,
      size: 'lg',
      injector: this.getTicketModalInjector()
    });
    const modalInstance = modalRef.componentInstance;
    modalInstance.ticket = this.ticket;
    modalInstance.emailType = EmailType.FORWARD;
    modalInstance.campaignName = EmailTrackingCampaignName.FORWARD;
    this.modal = modalInstance;
    modalRef.result
      .then(() =>
          this.toasterService.push({message: {messageKey: 'Email Sent'}, type: ToastType.Success}),
        // ignore if the modal was cancelled
        () => {
        })
      .finally(() => this.modal = null);
  }

  onEditComment(commentId: string) {
    const modalRef = this.modalService.open(UpsertTicketCommentModalComponent, { centered: true });
    const modalInstance: UpsertTicketCommentModalComponent = modalRef.componentInstance;
    modalInstance.ticket = this.ticket;
    modalInstance.comment = this.ticket.comments.find(c => c.comment_id === commentId);
    modalInstance.includeTicketUpdateFields = true;
    modalRef.result.then(({ comment, ticket }: UpsertTicketCommentResult) => {
      const foundCommentIndex = this.ticket.comments.findIndex(c => c.comment_id === comment.comment_id);
      if (foundCommentIndex >= 0) {
        const foundComment = this.ticket.comments[foundCommentIndex];
        Object.assign(foundComment, comment);
        this.onTicketChange(TicketUtils.convertAPIResultToOperationResult(TicketOperation.UPDATE, { ticket }));
      }
    }, (_dismissed) => {
    });
  }

  onAddCommentProgress(progress: UpsertTicketCommentProgress) {
    switch (progress.state) {
      case UpsertTicketCommentState.PROCESSING:
        this.updating = true;
        break;

      case UpsertTicketCommentState.DONE:
        // Need to rebuild the array so that Angular Change Detection knows the comments have changes
        const progressResult = progress.result;
        this.ticket.comments = [progressResult.comment, ...(this.ticket.comments || [])];
        this.ticket.comment_count = (this.ticket.comment_count || 0) + 1;
        this.commentIds.add(progressResult.comment.comment_id);
        this.commentOffset++;
        this.savedCommentText = null;
        this.updating = false;
        this.onTicketChange(TicketUtils.convertAPIResultToOperationResult(TicketOperation.UPDATE, { ticket: progressResult.ticket }));
        break;

      case UpsertTicketCommentState.ERROR:
        this.updating = false;
        // https://github.com/angular/angular/issues/15926
        throw progress.error instanceof HttpErrorResponse ? new Error(progress.error.message) : progress.error;
    }
  }

  onCommentFilesChange(updatedFiles: File[], clearUploadStatus?: boolean): void {
    this.resetFileSelectors();
    this.uploadStateComponent.uploadAttachments(updatedFiles, clearUploadStatus);
  }

  onCommentAttachmentOpen(attachmentId: string): void {
    this.attachmentsComponent.openAttachmentById(attachmentId);
  }

  setHasCommentAttachments(hasAttachments: boolean) {
    this.hasCommentAttachments = hasAttachments;
  }

  setCommentAttachmentUploading(uploading: boolean) {
    this.commentAttachmentUploading = uploading;
    this.cdRef.detectChanges();
  }

  /** Triggers delete in AttachmentsComponent for confirmation and api call. `AttachmentsComponent` will then emit the deleted attachment
   * that `deleteAttachment` here will handle to delete from ticket and `UploadStateComponent` */
  deleteAssociatedAttachmentById(attachmentId: string) {
    const attachmentToDelete = this.ticket.attachments.find(attachment => attachment.attachment_id === attachmentId);
    if (attachmentToDelete) {
      this.attachmentsComponent.onDeleteAttachment(attachmentToDelete);
    } else {
      this.logger.error('Expecting to find attachment with Id: "' + attachmentId + '" but found none');
      this.uploadStateComponent?.removeUploadByAttachmentId(attachmentToDelete.attachment_id);
    }
  }

  setAttachmentUploading(attachmentUploading: boolean) {
    this.attachmentUploading = attachmentUploading;
  }

  deleteAttachment(attachmentToDelete: TicketAttachment): void {
    this.uploadStateComponent?.removeUploadByAttachmentId(attachmentToDelete.attachment_id);
    const attachmentArray = [...this.ticket.attachments];
    const index = attachmentArray.findIndex(attachment => attachment.attachment_id === attachmentToDelete.attachment_id);
    attachmentArray.splice(index, 1);
    this.ticket.attachments = attachmentArray;
    this.onTicketChange();
  }

  onAttachmentsAssociated(uploadedAttachments: TicketAttachment[]) {
    this.ticketTrackingService.trackTicket(
      TicketTrackingEvent.UPLOAD_ATTACHMENT,
      this.ticket,
      {image_name_arr: uploadedAttachments.map(attachment => attachment.file_name)});
    this.ticket.attachments = [...uploadedAttachments, ...this.ticket.attachments];
    this.onTicketChange();
  }

  onAttachmentsFetched(fetchedAttachments: TicketAttachment[]) {
    this.ticket.attachments = fetchedAttachments;
    this.onTicketChange();
  }

  onAttachmentPreviewUserAction(previewActionEvent: AttachmentPreviewUserAction) {
    this.attachmentPreviewUserAction.emit(previewActionEvent);
  }

  toggleExpandComments(event: MouseEvent): void {
    event.stopPropagation();
    this.setCollapseComments(!this.commentsCollapsed);
  }

  expandComments(event: MouseEvent): void {
    // Ensure the click was not for a link
    if (!this.isDetectedLink(event)) {
      const selection = window.getSelection();
      // When text is being selected do not do anything, i.e. only expand collapsed comments if it's a simple click, rather than a selection
      if (!selection || !selection.toString()) {
        this.setCollapseComments(false);
      }
    }
  }

  toggleExpandTicketFields(): void {
    this.fieldsCollapsed = !this.fieldsCollapsed;
  }

  expandTicketFields(event: MouseEvent): void {
    // Ensure the click was not for a link
    if (!this.isDetectedLink(event)) {
      const selection = window.getSelection();
      // When text is being selected do not do anything, i.e. only expand collapsed fields if it's a simple click, rather than a selection
      if (!selection || !selection.toString()) {
        this.fieldsCollapsed = false;
      }
    }
  }

  submitComment(): void {
    if (this.commentComponent) {
      if (this.hasCommentAttachments) {
        const links: string[] = [];
        for (const attachment of this.uploadStateComponent.attachmentUploadState.uploadStatusByName.values()) {
          links.push(TicketUtils.makeAttachmentLink(
            this.ticket.site_id,
            this.ticket.ticket_id,
            attachment.uploadUrl.uuid,
            attachment.uploadUrl.file_name));
        }
        this.commentComponent.onSubmit(
          (comment: string) => (comment ? comment + '\n' : '') + links.join('\n'),
          () => this.uploadStateComponent.resetUploadForm(true)
        );
      } else {
        this.commentComponent.onSubmit();
      }
    }
  }

  onInfiniteScroll(): void {
    this.loadActivityIfNecessary(false);
  }

  haveMoreActivityToLoad(): boolean {
    return this.haveMoreCommentsToLoad() || (this.showChangeHistory && this.haveMoreChangeHistoryToLoad());
  }

  haveMoreCommentsToLoad(): boolean {
    return this.ticket?.comment_count == null || this.commentOffset < this.ticket.comment_count;
  }

  haveMoreChangeHistoryToLoad(): boolean {
    return this.ticket?.change_history_count == null || this.changeHistoryOffset < this.ticket?.change_history_count;
  }

  activityScrolled(): void {
    this.calculateCanScrollActivity();
  }

  scrollActivity(scrollDirection: 'up' | 'down'): void {
    if (this.activityContainer && this.activityContainer.nativeElement) {
      const el = this.activityContainer.nativeElement;
      if (scrollDirection === 'down') {
        el.scrollBy(0, this.activityContainerHeight);
        // Cleanup existing timeout if one exists
        if (this.activityScrollDelayHandle) {
          clearTimeout(this.activityScrollDelayHandle);
        }

        // When the scrollbar gets close enough to the bottom the activity start getting loaded, however, often the loading indicator is
        // "below the fold" and cannot be seen, because it is added to the end of the div when the activity starts getting loaded.
        // So using a setTimeout() wait, hopefully long enough, for the activity to start getting fetched, and if they still are being
        // fetched, and the scrollbar is close to the bottom (i.e. the user has not scrolled up in the mean time), scroll all the way down
        // so that the loading indicator can be seen
        this.activityScrollDelayHandle = setTimeout(() => {
          // Only scroll if the activity is being loaded
          if (this.loadingActivity) {
            const scrollDistanceFromBottom = this.scrollDistanceFromBottom(el);
            // and the scroll bar is close enough to the bottom. Would be great to be able to load the height of the loading indicator
            // dynamically, but at the moment 30 is close enough
            if (scrollDistanceFromBottom < 30) {
              el.scrollBy(0, scrollDistanceFromBottom);
            }
          }
        }, 200);
      } else if (scrollDirection === 'up') {
        el.scrollTop = 0;
      } else {
        throw new Error('Unknown scroll direction "' + scrollDirection + '"');
      }
    }
  }

  scrollDownForActivity(): boolean {
    return !this.isActivityScrollbarAtBottom || this.loadingActivity;
  }

  editTicket() {
    const modalRef = EscalatedTicketUpsertModalComponent.openInModal(this.modalService, {
      site: this.site,
      ticket: this.ticket,
    }, { injector: this.getTicketModalInjector() });

    this.modal = modalRef.componentInstance;

    modalRef.result
      .then(
        (result: TicketOperationResult<EscalatedTicket>) => this.onTicketChange(result),
        // Provide empty rejection handler due to https://github.com/ng-bootstrap/ng-bootstrap/issues/3721
        (_reason) => {
      })
      .finally(() => this.modal = null);
  }

  copyToClipboard(attachment: TicketAttachment): void {
    const attachmentLink = TicketUtils.makeAttachmentLink(
      this.ticket.site_id,
      this.ticket.ticket_id,
      attachment.attachment_id,
      attachment.file_name);
    BrowserUtils.copyToClipboard(attachmentLink);
    this.toasterService.push(this.COPY_TO_CLIPBOARD_TOAST);
  }

  private getTicketModalInjector(): Injector {
    if (!this.ticketModalInjector) {
      this.ticketModalInjector = TicketUtils.makeTicketDataCachesInjector(this.ticketDataCaches, this.injector);
    }
    return this.ticketModalInjector;
  }

  private fetchChangeHistoryAfterUpdate(): void {
    // If the ticket has nay change history 'cached' on it, then fetch the most recent change history
    if (this.ticket.change_history != null) {
      if (this.showChangeHistory) {
        // Only show the spinner if change history is shown
        this.loadingChangeHistory = true;
      }
      const ticketChangeHistory = this.ticket.change_history?.length ? this.ticket.change_history[0] : null;
      const FETCH_SIZE = 5;
      const MAX_REQUESTS = 10;
      let attempt = 0;
      const usefulChangeHistory: TicketChangeHistory[] = [];
      const fetchChangeHistoryFn = (offset: number) => this.ticketsService.getChangeHistoryForEscalatedTicket(this.ticket.ticket_id, offset, FETCH_SIZE);
      fetchChangeHistoryFn(0).pipe(
          expand(({ count: _historyCount, change_history: changeHistory }) => {
            const newChangeHistory: TicketChangeHistory[] = [];
            for (const ch of (changeHistory || [])) {
              if (ch && (!ticketChangeHistory || (ch.ticket_change_history_id !== ticketChangeHistory.ticket_change_history_id && !ch.ts.isBefore(ticketChangeHistory.ts)))) {
                newChangeHistory.push(ch);
              } else {
                break;
              }
            }

            if (newChangeHistory.length) {
              usefulChangeHistory.push(...newChangeHistory);
              if (newChangeHistory.length >= FETCH_SIZE) {
                // Keep fetching
                attempt++;
                if (attempt < MAX_REQUESTS) {
                  return fetchChangeHistoryFn(attempt * FETCH_SIZE);
                } else {
                  this.logger.error('Could not retrieve all of Change History for ticket', this.ticket?.ticket_id);
                  return EMPTY;
                }
              } else {
                return EMPTY;
              }
            } else {
              if (attempt === 0) {
                this.logger.error('Latest Change History on the ticket', ticketChangeHistory, 'is newer than retrieved Change History');
              }
              return EMPTY;
            }
          }),
          reduce((acc, result) => ({ count: result.count, change_history: acc.change_history }), { count: null, change_history: usefulChangeHistory }),
          finalize(() => this.loadingChangeHistory = false)
      ).subscribe(({ count: historyCount, change_history: changeHistory }) => {
        if (historyCount != null) {
          this.ticket.change_history_count = historyCount;
        }

        if (changeHistory?.length) {
          for (let i = changeHistory.length - 1; i >= 0; i--) {
            const ch = changeHistory[i];
            if (!this.changeHistoryIds.has(ch.ticket_change_history_id)) {
              this.ticket.change_history.unshift(ch);
              this.changeHistoryIds.add(ch.ticket_change_history_id);
              this.changeHistoryOffset++;
            }
          }
          this.ticket.change_history = [...this.ticket.change_history];
          this.scrollActivity('up');
          this.resetActivityScrollBarStateCalculation();
        }
      });
    }
  }

  viewLinkedAlert(ticket: Ticket): void {
    const alert = <AlertTicket>ticket;
    const modalInput: AlertTicketDetailsModal = {
      ticket: alert,
      site: this.site,
      showSiteName: false,
      showViewLinkedTicket: false,
      readOnly: this.readOnly
    };
    const modalOptions: NgbModalOptions = {
      keyboard: false,
      injector: this.getTicketModalInjector()
    };
    const modalRef = AlertTicketDetailsModalComponent.openInModal(this.modalService, modalInput, modalOptions);
    this.modal = modalRef.componentInstance;
    modalRef.result
      .then(
        result => this.onTicketChange(result),
        // Provide empty rejection handler due to https://github.com/ng-bootstrap/ng-bootstrap/issues/3721
        (_reason) => {})
      .finally(() => this.modal = null);
  }

  private isDetectedLink(event: MouseEvent): boolean {
    return event.target instanceof Element && event.target.classList.contains(this.linkifyService.LINKY_CSS_CLASS_NAME);
  }

  private setCollapseComments(value: boolean): void {
    this.commentsCollapsed = value;
    if (this.commentsCollapsed) {
      this.saveTypedComment();
      if (this.addCommentConfig != null) {
        this.addCommentConfig.showAddComment = false;
      }
    }
    this.resetActivityScrollBarStateCalculation();
  }

  private resetActivityScrollBarStateCalculation(): void {
    this.doesActivityScrollbarExist = null;
    this.isActivityScrollbarAtBottom = null;
  }

  private collapseFieldValue(value: string): string {
    // Build a new matcher from the constant as the matcher is stateful
    const matcher = new RegExp(this.NEW_LINES_REG_EXP, this.NEW_LINES_REG_EXP.flags);
    const result = matcher.exec(value);
    // If the text has more than one line (i.e. matcher.test(value) returns true), append '...' to indicate the text is collapsed
    return result ? result[0].trim() + (matcher.test(value) ? ' ...' : '') : null;
  }

  isActionInProgress(): boolean {
    return this.actionStateManager.isActionInProgress();
  }

  isActionExecuting(action: EscalatedTicketExecutableAction): boolean {
    return this.actionStateManager.isActionExecuting(action);
  }

  isActionAvailable(action: EscalatedTicketExecutableAction): boolean {
    return this.actionStateManager.isActionAvailable(action, this.ticket?.status?.status_id ?? null);
  }

  private resetFileSelectors(): void {
    // If the expanded ticket view was collapsed the directives are destroyed, and set to null, so need to check for null first.
    // Note, that when the view is expanded the directives are recreated (with empty 'files'), so no need to worry about what happens
    // if the view got collapsed during upload
    if (this.commentUploadButtonSelectDirective) {
      this.commentUploadButtonSelectDirective.files = [];
    }
  }

  private loadActivityIfNecessary(showLoadChangeHistory: boolean): void {
    if (this.haveMoreActivityToLoad()) {
      this.loadActivityEvent.next(showLoadChangeHistory);
    }
  }

  private calculateCanScrollActivity(): void {
    if (this.activityContainer && this.activityContainer.nativeElement) {
      const el = this.activityContainer.nativeElement;
      this.doesActivityScrollbarExist = el.scrollHeight > el.clientHeight;
      this.isActivityScrollbarAtBottom = this.scrollDistanceFromBottom(el) < 1;
    } else {
      this.doesActivityScrollbarExist = false;
      this.isActivityScrollbarAtBottom = false;
    }
  }

  private scrollDistanceFromBottom(el: HTMLElement): number {
    return el.scrollHeight - el.scrollTop - el.clientHeight;
  }

  saveLinkedAlerts() {
    const callbackFunctions: LinkActionToAlertsCallbackFns = {
      onConfirmUpdateLink: this.doSaveLinkedAlerts.bind(this),
    };
    TicketUtils.linkActionToAlerts(this.ticket, this.linkedAlertsControl.value, this.modalService, callbackFunctions);
  }

  private reloadTasks(taskIds: number[]) {
    if (taskIds?.length) {
      this.tasksLoading = true;
      this.ticketDataCaches.getTasksByIds(taskIds)
        .pipe(finalize(() => this.tasksLoading = false))
        .subscribe(tasksById => this.tasksById = tasksById);
    } else {
      this.tasksById = null;
    }
  }

  private doSaveLinkedAlerts(alerts: AlertTicket[] | null) {
    this.linkedAlertSelect.setEditable(false);
    alerts?.forEach(alert => TicketUtils.registerTicketInMap(alert, this.ticketsById));
    this.actionStateManager.setExecutingAction(EscalatedTicketExecutableAction.LINK_TICKET, true);
    const escalatedTicketsById = CollectionUtils.indexBy(alerts?.map(TicketUtils.getAlertLinkedAction).filter(Boolean), 'ticket_id');
    escalatedTicketsById.set(this.ticket.ticket_id, this.ticket);
    const oldTaskIdsByTicketId = TicketUtils.getTaskIdsByTicketId(escalatedTicketsById.values());
    this.ticketsService.linkActionToAlerts(this.ticket, alerts, this.ticketReferenceData)
      .pipe(
        tap(() => this.reloadTasks(alerts?.map(alert => alert.rule_id))),
        finalize(() => this.actionStateManager.setExecutingAction(EscalatedTicketExecutableAction.LINK_TICKET, false)))
      .subscribe((result: TicketAPIResultFull<EscalatedTicket>) => {
        const newTaskIdsByTicketId = TicketUtils.getTaskIdsByTicketId(escalatedTicketsById.values());
        this.ticketDataCaches.updateRuleTicketCache(oldTaskIdsByTicketId, newTaskIdsByTicketId, escalatedTicketsById);
        this.onTicketChange(TicketUtils.convertAPIResultToOperationResult(TicketOperation.UPDATE, result));
      });
  }

  cancelEditLinkedAlerts() {
    this.refreshLinkedAlerts();
  }

  private setShowChangeHistory(showChangeHistory: boolean): void {
    this.showChangeHistory = showChangeHistory;
    if (this.showChangeHistory) {
      this.scrollActivity('up');
      this.setCollapseComments(false);
      // If Change History is being shown and none is loaded, then fetch it
      if (this.ticket.change_history == null) {
        this.loadActivityEvent.next(true);
      }
    }
    this.resetActivityScrollBarStateCalculation();
  }

  toggleActivity(): void {
    this.setShowChangeHistory(!this.showChangeHistory);
    this.ticketTrackingService.trackTicket(this.showChangeHistory ? TicketTrackingEvent.SHOW_ACTIVITY : TicketTrackingEvent.HIDE_ACTIVITY, this.ticket);
  }

  // region // ticket change handlers
  private isUpdatedTicketPartForCurrentTicket(ticketUpdatePart: Partial<Ticket> | null): boolean {
    return ticketUpdatePart?.ticket_id == null || ticketUpdatePart?.ticket_id === this.ticket.ticket_id;
  }

  private updateTicket(ticketUpdatePart: Partial<Ticket>): void {
    if (this.isUpdatedTicketPartForCurrentTicket(ticketUpdatePart)) {
      this.afterTicketUpdate();
    }
  }

  /**
   * When ticket was changed within this component, we NEED to invoke this method to inform the parent component.
   *
   * @param result: if presented, the value will be merged into this.ticket
   * @private
   */
  private onTicketChange(result?: TicketOperationResult<Ticket>): void {
    const ticketUpdatePart = result?.ticket;

    if (result?.operation !== TicketOperation.DELETE) {
      this.updateTicket(ticketUpdatePart);
    }

    result?.affectedTickets?.forEach(affectedTicket => this.updateTicket(affectedTicket));

    this.ticketOperation.next({
      ...result,
      // ticket property in TicketOperationResult should always be a full ticket object rather than Partial.
      ticket: this.isUpdatedTicketPartForCurrentTicket(ticketUpdatePart) ? this.ticket : this.ticketsById.get(ticketUpdatePart.ticket_id)
    });
  }

  /**
   * When ticket was changed, this method needs to be invoked to make sure this component the latest change history
   * and all calculated fields are refreshed.
   */
  afterTicketUpdate(): void {
    this.saveTypedComment();
    this.refreshTicketCalculatedFields();
    this.fetchChangeHistoryAfterUpdate();
  }

  // endregion

  onCommentBoxFocus(focused: boolean): void {
    if (this.commentBoxFocusedDelayHandle != null) {
      clearTimeout(this.commentBoxFocusedDelayHandle);
    }
    // There seems to be an Angular bug that happens whenever a variable that's used in the View is modified as a result
    // of a blur event. The click gets "lost". This happens on all web browsers. So, without the setTimeout() call, if
    // the the Add Comment text area is shown, and the user clicks on e.g. "Expand Comments" chevron, the click does
    // not reach teh chevron. The bug does NOT happen when Chrome Dev Tools are shown, but as most users do not have
    // Dev Tools open in production, the bug happens in prod :) The delay seems to help.
    this.commentBoxFocusedDelayHandle = setTimeout(() => {
      this.commentBoxFocused = focused;
      this.commentBoxFocusedDelayHandle = null;
    }, 200);
  }

  onResizeEnd(event: ResizeEvent): void {
    this.manuallySetHeight = true;
    this.activityContainerHeight = event.rectangle.height;
    this.resetActivityScrollBarStateCalculation();
    this.userProfileService.updateActivityContainerHeightUserPreference(this.activityContainerHeight);
  }

  resetActivityHeight(): void {
    this.manuallySetHeight = false;
    this.activityContainerHeight = this.DEFAULT_ACTIVITY_CONTAINER_HEIGHT;
    this.userProfileService.updateActivityContainerHeightUserPreference(null);
    this.resetActivityScrollBarStateCalculation();
  }

  displayDeactivateConfirmation(): boolean {
    return this.hasCommentAttachments || this.commentComponent?.hasText() || this.attachmentUploading;
  }

  getDeactivateModalOptions(): DeactivateModalOptions {
    if (this.hasCommentAttachments || this.commentComponent?.hasText()) {
      return this.commentDeactivateOption;
    } else if (this.attachmentUploading) {
      return this.uploadingDeactivateOption;
    }
  }

  addMention(assignee: User): void {
    if (assignee?.email) {
      this.commentComponent.addMention(this.MENTION_CHARACTER + assignee.email);
    }
  }

  updateMentionLastFocus(lastFocused: boolean): void {
    this.mentionLastFocused = lastFocused;
  }

  upsertTimeTaken(): void {
    const modalRef = DurationInputModalComponent.openInModal(this.modalService, {
      duration: this.ticket.time_taken ?? null,
      units: ['hour', 'minute'],
      saveFn: duration => this.ticketsService.upsertActionTimeTaken(this.ticket, duration, this.ticketReferenceData)
    });
    modalRef.result.then((result: TicketAPIResultFull<EscalatedTicket>) =>
      this.onTicketChange(TicketUtils.convertAPIResultToOperationResult(TicketOperation.UPDATE, result)),
      () => {});
  }
}
